<template>
  <section class="section content-container">
    <n-loading-spinner v-if="isLoading" />

    <div class="container is-fluid" v-if="!isLoading">
      <div class="columns">
        <div class="column">
          <TitledSection class="container is-fluid island">
            <template v-slot:title>Email templates</template>
            <template v-slot:subtitle>Customise emails sent to your users with your company branding</template>
            <template v-slot:actions>
              <n-button id="create-new-email-template-button" variant="primary" @click="openTemplateEditorForCreate">
                Create template
              </n-button>
            </template>

            <div>
              <div class="columns">
                <div class="column is-6">
                  <div class="field">
                    <label class="label" for="activeTemplate">Select the active template</label>
                    <div class="control select">
                      <select id="activeTemplate" v-model="activeTemplateId">
                        <option value selected>-- Default Template --</option>
                        <option v-for="template in templates" :key="template.templateId" :value="template.templateId">
                          {{ template.name }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <table class="table is-fullwidth" v-if="hasTemplates">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>

                <tr class="templateRow" v-for="template in templates" :key="template.templateId">
                  <td>
                    <a :id="'edit-template-link-' + template.templateId" @click="editTemplate(template)">{{
                      template.name
                    }}</a>
                  </td>
                  <td class="template-status-column">
                    <Badge :type="getStatusClass(template)">{{ getStatus(template) }}</Badge>
                  </td>
                  <td class="template-icons-column">
                    <span
                      :id="'delete-template-' + template.templateId"
                      @click="showDeleteTemplateConfirmation(template)"
                      class="icon is-small"
                    >
                      <font-awesome-icon icon="trash" />
                    </span>
                  </td>
                </tr>
              </table>
            </div>

            <Modal v-if="showDeleteTemplateModal" @cancel="showDeleteTemplateModal = false">
              <template v-slot:title>
                <p class="modal-card-title">Delete email template</p>
              </template>

              <template v-slot:body>
                <label class="label">
                  Are you sure you want to delete template '{{ deleteTemplateModel?.name }}'?
                  <p v-if="deleteTemplateModel?.templateId === activeTemplateId">
                    Upon delete, the default Connect template will become the active template.
                  </p>
                </label>
              </template>

              <template v-slot:buttons>
                <n-button-group>
                  <n-button variant="primary" @click="deleteTemplate">Delete </n-button>
                  <n-button @click="showDeleteTemplateModal = false" variant="primary--outline">Cancel </n-button>
                </n-button-group>
              </template>
            </Modal>
          </TitledSection>

          <TitledSection class="container is-fluid island">
            <template v-slot:title>Outbound email address</template>
            <template v-slot:subtitle>Send emails from your company email address</template>
            <div class="columns domain-body">
              <div class="column is-6">
                <div class="field" v-if="!domainSaved">
                  <label class="label" for="domain">Enter your company domain</label>
                  <div class="control">
                    <input
                      type="text"
                      class="input"
                      id="domain"
                      placeholder="E.g. yourdomain.com"
                      v-model="domain"
                      @input="$v.domain.$touch()"
                      :class="{ 'is-danger': $v.domain.$invalid && $v.domain.$dirty }"
                    />
                    <p class="help is-danger" v-if="$v.domain.$invalid && !$v.domain.required && $v.domain.$dirty">
                      Domain is required
                    </p>
                  </div>
                </div>

                <div v-if="domainSaved">
                  <Badge :type="domainVerified ? 'success' : 'danger'" size="medium">
                    <b>{{ domainVerified ? 'Verified' : 'Unverified' }} : </b>
                    &nbsp;
                    {{ domain }}
                  </Badge>

                  <span id="delete-domain-button" @click="deleteDomain" class="icon is-small">
                    <font-awesome-icon icon="trash"></font-awesome-icon>
                  </span>
                </div>

                <div v-if="!domainVerified && domainSaved">
                  <div class="padding-above" v-if="!domainVerified || !domainSaved">
                    The following DNS records need adding before domain can be verified and emails sent.

                    <table class="margin-above table is-fullwidth">
                      <tr>
                        <th class="domain-key-column">Name</th>
                        <td>connect._domainkey.{{ domain ? domain : '&lt;your_domain&gt;' }}</td>
                      </tr>
                      <tr>
                        <th>Type</th>
                        <td>CNAME</td>
                      </tr>
                      <tr>
                        <th>Value</th>
                        <td>dkim.onconnect.app</td>
                      </tr>
                    </table>
                  </div>

                  <div class="margin-above" v-if="domainSaved">
                    <n-button
                      id="verify-domain-button"
                      @click="verifyDomain"
                      :loading="verifyInProgress"
                      variant="primary"
                    >
                      <span>Verify Domain</span>
                    </n-button>
                  </div>
                </div>
              </div>
            </div>
          </TitledSection>

          <n-button
            id="save-template-button"
            @click="save"
            variant="primary"
            :loading="updateInProgress"
            :disabled="!canSave()"
          >
            Save
          </n-button>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import { emailApi } from '@/features/settings/services/emailApiService';
import Modal from '@/shared/components/Modal.vue';
import { EmailTemplateViewModel } from '@/features/settings/models/emailTemplateViewModel';
import TitledSection from '@/shared/components/TitledSection.vue';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import Toast from '@/shared/services/toast';
import { maxLength } from '@vuelidate/validators';
import { EmailWorkspaceModel } from '@/features/settings/models/response/emailWorkspaceModel';
import Badge from '@/shared/components/Badge.vue';
import { useStore } from 'vuex';
import { computed, onMounted, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { useRouter } from 'vue-router';

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);

const isLoading = ref(true);
const updateInProgress = ref(false);
const verifyInProgress = ref(false);
const showDeleteTemplateModal = ref(false);
const deleteTemplateModel = ref<EmailTemplateViewModel>();
const activeTemplateId = ref('');
const lastActiveTemplateId = ref('');
const domain = ref('');
const domainVerified = ref(false);
const domainSaved = ref(false);
const templates = ref<EmailTemplateViewModel[]>([]);
const hasTemplates = computed(() => templates.value.length > 0);

// should reflect what is currently stored on the server, only change with responses from the server
const savedEmailWorkspace = ref<EmailWorkspaceModel>();

const validations = {
  activeTemplateId: {},
  domain: { maxLength: maxLength(67) },
};

const $v = useVuelidate(validations, { activeTemplateId, domain });

const router = useRouter();

onMounted(async () => {
  try {
    await loadTemplates();
  } catch (error) {
    if (error.status !== 404) {
      handleSeriousError(error.message);
    }
  } finally {
    isLoading.value = false;
  }
});

async function loadTemplates() {
  templates.value = await emailApi.getTemplates(currentWorkspace.value.id);
  await loadConfiguration();
}

async function loadConfiguration() {
  const response: EmailWorkspaceModel = await emailApi.getEmailWorkspace(currentWorkspace.value.id);
  setConfig(response);
}

function setConfig(emailWorkspace: EmailWorkspaceModel) {
  savedEmailWorkspace.value = emailWorkspace;

  domain.value = emailWorkspace.outboundEmailDomain;
  domainVerified.value = emailWorkspace.outboundEmailDomainVerified;
  domainSaved.value = !!domain.value;

  activeTemplateId.value = emailWorkspace.activeTemplateId;
  lastActiveTemplateId.value = emailWorkspace.activeTemplateId;
}

function getStatus(template: EmailTemplateViewModel): string {
  const templateActive = template.templateId === lastActiveTemplateId.value;
  if (templateActive) {
    return 'Active';
  } else {
    return 'Inactive';
  }
}

function getStatusClass(template: EmailTemplateViewModel): 'success' | 'disabled' {
  const templateActive = template.templateId === lastActiveTemplateId.value;
  if (templateActive) {
    return 'success';
  } else {
    return 'disabled';
  }
}

function openTemplateEditorForCreate() {
  router.push({ name: 'email-template-create', params: {} });
}

function editTemplate(template: EmailTemplateViewModel) {
  router.push({
    name: 'email-template-edit',
    params: { templateId: template.templateId },
    query: { templateName: template.name },
  });
}

function showDeleteTemplateConfirmation(template: EmailTemplateViewModel) {
  showDeleteTemplateModal.value = true;
  deleteTemplateModel.value = template;
}

async function deleteTemplate() {
  try {
    await emailApi.deleteEmailTemplate(currentWorkspace.value.id, deleteTemplateModel.value!.templateId);
    loadTemplates();
  } catch (err) {
    Toast.showError(`Failed to delete template ${err}`);
  } finally {
    showDeleteTemplateModal.value = false;
  }
}

function canSave() {
  const invalid = $v.value.$invalid;
  const activeTemplateTouched = activeTemplateId.value !== lastActiveTemplateId.value;
  const domainTouched = $v.value.domain.$dirty;
  return (activeTemplateTouched || domainTouched) && !invalid;
}

async function save() {
  try {
    updateInProgress.value = true;
    const response: EmailWorkspaceModel = await emailApi.saveEmailWorkspace(currentWorkspace.value.id, {
      activeTemplateId: activeTemplateId.value,
      outboundEmailDomain: domain.value,
    });
    setConfig(response);
    Toast.showSuccess(`Email configuration successfully saved`);
  } catch (err) {
    Toast.showError(`Failed to save email configuration ${err}`);
  } finally {
    updateInProgress.value = false;
  }
}

async function deleteDomain() {
  const response: EmailWorkspaceModel = await emailApi.saveEmailWorkspace(currentWorkspace.value.id, {
    activeTemplateId: savedEmailWorkspace.value!.activeTemplateId,
    outboundEmailDomain: '',
  });

  Toast.showSuccess('Successfully cleared company domain');
  setConfig(response);
}

async function verifyDomain() {
  try {
    verifyInProgress.value = true;
    const verifyResponse = await emailApi.verifyDomain(currentWorkspace.value.id);

    if (verifyResponse.success) {
      domainVerified.value = true;
      Toast.showSuccess(`Domain is now verified`);
    } else {
      Toast.showInfo(verifyResponse.errorMessage);
    }
  } catch (err) {
    Toast.showError(`Failed to verify domain. ${err.message}`);
  } finally {
    verifyInProgress.value = false;
  }
}
</script>

<style scoped lang="scss">
.padding-above {
  padding-top: 20px;
}

.margin-above {
  margin-top: 20px;
}

.template-status-column {
  width: 150px;
}

.template-icons-column {
  text-align: right;

  span {
    float: right;
  }
}

.island {
  min-height: 180px;
}

.domain-key-column {
  width: 100px;
}

select,
.select {
  width: 100%;
}

@media (max-width: 900px) {
  .templates-body {
    margin-top: 0;
  }
}

.label p {
  margin-top: 10px;
}

.icon {
  cursor: pointer;
}

.is-info {
  cursor: pointer;
}

#delete-domain-button {
  vertical-align: middle;
  margin-left: 5px;
}

.domain-body {
  flex: 1;
}

section {
  flex: 1;
}
</style>
